import { useEffect, useState } from "react";
import { useShipmentDraftsApi } from "@/Hooks/useShipmentDraftsApi";
import FullWidthLayout from "@/Shared/Layout/CustomComponents/FullWidthLayout";
import type { ShipmentDraft } from "@freightsimple/generated-dashboard-openapi-client/models/ShipmentDraft";
import { Heading } from "@freightsimple/ui-core/Typography/Heading";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { T } from "@freightsimple/ui-core/Typography/T";
import { ShipmentDraftsRow } from "./CustomComponents/ShipmentDraftsRow";

export function ShipmentDraftsScreen() {
  const draftsApi = useShipmentDraftsApi();
  const [isLoading, setIsLoading] = useState(false);
  const [drafts, setDrafts] = useState<ShipmentDraft[]>([]);
  const [loadFailed, setLoadFailed] = useState(false);

  async function fetchDrafts() {
    try {
      setIsLoading(() => true);
      setLoadFailed(false);
      const response = await draftsApi.getShipmentDrafts();
      setDrafts(response);
    } catch (e) {
      console.error(e);
      setLoadFailed(true);
    } finally {
      setIsLoading(() => false);
    }
  }

  useEffect(() => {
    fetchDrafts();
  }, []);

  return (
    <FullWidthLayout
      header="Drafts"
      microcopy="Review and complete these shipments"
      loading={isLoading}
      showEmptyState={drafts.length === 0}
      onRetryFailedLoad={loadFailed ? fetchDrafts : undefined}
      emptyState={
        <Stack gap={2}>
          <Heading level={2}>You don't have any drafts</Heading>
          <T color="secondary">
            Drafts are created via API. Use our integration to send shipment
            details and complete booking here.
          </T>
        </Stack>
      }
      content={
        <Stack gap={4} align="start">
          {drafts.map((d) => (
            <ShipmentDraftsRow
              key={d.shipmentDraftId}
              draft={d}
              onRefresh={fetchDrafts}
            />
          ))}
        </Stack>
      }
    />
  );
}
